import { useRef, useState } from "react";
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useSpring } from "framer-motion";

/**
 * Pipeline stepper whose rail fills as the section scrolls through the
 * viewport, lighting each stage in turn.
 *
 * Scrubbing is scoped to this section's own scroll range so it does not scan
 * the whole page. Under reduced motion the rail is drawn full and every stage
 * is lit from the start.
 */
function Stepper({ steps }) {
  const prefersReducedMotion = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.8", "end 0.6"],
  });
  const fill = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.6 });
  const [lit, setLit] = useState(prefersReducedMotion ? steps.length : 0);

  useMotionValueEvent(scrollYProgress, "change", (value) => {
    if (prefersReducedMotion) return;
    // Light a stage slightly before the rail reaches it, so the fill appears
    // to arrive at an already-active node rather than lag behind it.
    setLit(Math.min(steps.length, Math.floor(value * steps.length + 0.45)));
  });

  return (
    <div
      className="steps"
      ref={ref}
      // Column count follows the step count so the paper's stages can be shown
      // in full rather than being squeezed into a fixed four.
      style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}
    >
      <div className="steps__rail" aria-hidden="true">
        <motion.div
          className="steps__rail-fill"
          style={{ scaleX: prefersReducedMotion ? 1 : fill }}
        />
      </div>

      {steps.map((step, index) => (
        <div className="step" key={step.title} data-lit={prefersReducedMotion || index < lit}>
          <span className="step__num">
            <step.icon size={19} aria-hidden="true" />
          </span>
          <span className="step__tick">
            {String(index + 1).padStart(2, "0")} / {String(steps.length).padStart(2, "0")}
          </span>
          <h3 className="step__title">{step.title}</h3>
          <p className="step__body">{step.body}</p>
        </div>
      ))}
    </div>
  );
}

export default Stepper;
